'use client';

import React, { useState } from 'react';

import { Heading } from '../../../../components/Heading/Heading';
import { Text } from '../../../../components/Text/Text';
import { MILLION_USERS } from '../../../../constants/constants';
import { GetSpeciesTopBaits_topProductUnits_edges_node } from '../../../../interfaces/graphql';
import { twMerge } from 'tailwind-merge';
import { SUB_LINK_IDS } from '../../helpers/speciesHelpers';
import { Button } from '../../../../components/Clickables/Buttons/Button';
import { useAnalytics } from '../../../../contexts/AnalyticsContext/AnalyticsContext';
import { trackEvent } from '../../../../helpers/analytics/trackEvent';
import { TopBaitsEntry } from './components/TopBaitsEntry';
import { TopBaitsSectionCTA } from './components/TopBaitsSectionCTA';
import { AppDownloadModal } from '../../../../components/AppDownloadModal/AppDownloadModal';

interface IProps {
  className?: string;
  topBaits?: GetSpeciesTopBaits_topProductUnits_edges_node[];
  speciesId: string;
  speciesName: string;
}

export const TopBaitsSection = ({ className, topBaits, speciesName, speciesId }: IProps) => {
  const { currentPage } = useAnalytics();
  const [isAppDownloadModalOpen, setIsAppDownloadModalOpen] = useState(false);

  const handleCTAClick = () => {
    void trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: 'TopBaitsSection',
        source_id: speciesId,
        type: 'downloadModal',
        page: currentPage,
      },
    });
    setIsAppDownloadModalOpen(true);
  };

  return (
    <div
      id={SUB_LINK_IDS.TOP_BAITS}
      className={twMerge(
        'sm:bg-lake-fog scroll-mt-(--navigation-scrolltop-offset-mobile) sm:scroll-mt-(--navigation-scrolltop-offset) sm:rounded-2xl',
        className,
      )}
    >
      <Heading className="mx-auto text-left" variant="sm">
        Best baits to catch {speciesName}
      </Heading>
      <Text className="my-5">
        {`According to catch data from over ${MILLION_USERS} million anglers on Fishbrain, these are the best baits used to catch ${speciesName}.`}
      </Text>

      <div className="flex flex-col">
        <TopBaitsSectionCTA speciesId={speciesId} />
        {topBaits?.map(({ productUnit, numberOfCatches, position }) => {
          return (
            <TopBaitsEntry
              key={`species-${productUnit.id}`}
              productUnit={productUnit}
              numberOfCatches={numberOfCatches}
              position={position}
              onClick={handleCTAClick}
            />
          );
        })}
        <Button size="sm" className="mt-2 p-2" variant="textPrimary" onClick={handleCTAClick}>
          Show more baits
        </Button>
      </div>
      <AppDownloadModal
        appDownloadSource="TopBaitsSection"
        appDownloadSourceId={speciesId}
        modalHeader={`Find out what lures to use for catching ${speciesName}`}
        isModalOpen={isAppDownloadModalOpen}
        onCloseModal={() => setIsAppDownloadModalOpen(false)}
      />
    </div>
  );
};
